import {
    Questionnaire,
    QuestionnaireItem,
    QuestionnaireResponse,
    QuestionnaireResponseItem,
    QuestionnaireResponseItemAnswer,
} from '../../types/fhir';

export type TherapyMaterialAudience = 'female' | 'male';

export type TherapyMaterialContentItem = {
    type: string;
    text?: string;
    path?: string;
    w?: number;
    h?: number;
    // Optional caption shown above an image.
    header?: string;
    character?: string;
    rows?: number;
    prompts?: string[];
};

export type TherapyMaterialDocument = {
    id: string;
    title: string;
    content: Partial<Record<TherapyMaterialAudience, TherapyMaterialContentItem[] | null>>;
    week?: number | string;
};

export type TherapyMaterialAnswer = {
    linkId: string;
    text?: string;
    item: QuestionnaireResponseItem;
};

type TherapyMaterialDefaults = {
    id?: string;
    title?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isContentItem = (value: unknown): value is TherapyMaterialContentItem =>
    isRecord(value) && typeof value.type === 'string';

const parseAudience = (value: unknown): TherapyMaterialContentItem[] | null => {
    if (value === null || value === undefined) {
        return null;
    }

    if (!Array.isArray(value) || !value.every(isContentItem)) {
        throw new Error('Invalid therapy material content');
    }

    return value;
};

export const parseTherapyMaterialPayload = (
    rawPayload: string,
    defaults: TherapyMaterialDefaults = {},
): TherapyMaterialDocument => {
    let parsed: unknown = rawPayload;

    // Some older resources contain a JSON string that was encoded more than once.
    for (let depth = 0; depth < 3 && typeof parsed === 'string'; depth += 1) {
        parsed = JSON.parse(parsed);
    }

    if (!isRecord(parsed)) {
        throw new Error('Invalid therapy material document');
    }

    const id = (typeof parsed.id === 'string' ? parsed.id : defaults.id)?.trim() ?? '';
    const title = (typeof parsed.title === 'string' ? parsed.title : defaults.title)?.trim() ?? '';
    let content: TherapyMaterialDocument['content'];

    if (Array.isArray(parsed.content)) {
        // Stored therapy material created by the original upload flow already contains the
        // selected content variant as one flat array. Keep it as the sole available variant.
        content = {
            female: parseAudience(parsed.content),
            male: null,
        };
    } else if (isRecord(parsed.content)) {
        // Newer source files keep separate variants which the preview can switch between.
        content = {
            female: parseAudience(parsed.content.female),
            male: parseAudience(parsed.content.male),
        };
    } else {
        throw new Error('Invalid therapy material content');
    }

    if (!id || !title || (!content.female?.length && !content.male?.length)) {
        throw new Error('Incomplete therapy material document');
    }

    return { id, title, content };
};

export const getTherapyMaterialDocument = (questionnaire: Questionnaire): TherapyMaterialDocument => {
    const payloadItem = questionnaire.item?.[0];
    const payload = payloadItem?.text;
    if (!payload?.trim()) {
        throw new Error('Therapy material payload is missing');
    }

    return parseTherapyMaterialPayload(payload, {
        id: payloadItem?.linkId || questionnaire.id,
        title: questionnaire.title || questionnaire.name,
    });
};

export const getNachsorgeMaterialDocument = (questionnaire: Questionnaire): TherapyMaterialDocument => {
    const metadataPayload = questionnaire.item?.[0]?.text;
    const questionItem = questionnaire.item?.[1];
    const questionText = questionItem?.text?.trim();
    const title = (questionnaire.title || questionnaire.name)?.trim();

    if (!metadataPayload?.trim() || !questionItem || !questionText || !title) {
        throw new Error('Incomplete aftercare material');
    }

    let parsedMetadata: unknown = metadataPayload;
    for (let depth = 0; depth < 3 && typeof parsedMetadata === 'string'; depth += 1) {
        parsedMetadata = JSON.parse(parsedMetadata);
    }

    if (!isRecord(parsedMetadata)) {
        throw new Error('Invalid aftercare metadata');
    }

    const rawWeek = parsedMetadata.week;
    const week =
        typeof rawWeek === 'number' && Number.isFinite(rawWeek)
            ? rawWeek
            : typeof rawWeek === 'string' && rawWeek.trim()
            ? rawWeek.trim()
            : null;

    if (week === null) {
        throw new Error('Aftercare week is missing');
    }

    return {
        id: questionnaire.id || questionItem.linkId || `nachsorge-${week}`,
        title,
        week,
        content: {
            female: [{ type: 'textfield', text: questionText }],
            male: null,
        },
    };
};

export const getMaterialDocument = (
    questionnaire: Questionnaire,
    kind: 'therapy-material' | 'nachsorge',
): TherapyMaterialDocument =>
    kind === 'nachsorge' ? getNachsorgeMaterialDocument(questionnaire) : getTherapyMaterialDocument(questionnaire);

export const getAvailableAudiences = (document: TherapyMaterialDocument): TherapyMaterialAudience[] =>
    (['female', 'male'] as TherapyMaterialAudience[]).filter((audience) => Boolean(document.content[audience]?.length));

// The app shows the female version unless the patient picked the male voice, which the editor
// cannot see, so the female version is the default whenever it exists.
export const getDefaultAudience = (document: TherapyMaterialDocument): TherapyMaterialAudience =>
    getAvailableAudiences(document)[0] ?? 'female';

const visitResponseItems = (
    items: QuestionnaireResponseItem[] | undefined,
    answers: Map<string, TherapyMaterialAnswer>,
): void => {
    items?.forEach((item) => {
        if (item.linkId) {
            answers.set(item.linkId, {
                linkId: item.linkId,
                text: item.text,
                item,
            });
        }

        visitResponseItems(item.item, answers);
        item.answer?.forEach((answer) => visitResponseItems(answer.item, answers));
    });
};

export const getTherapyMaterialAnswers = (
    questionnaireResponse?: QuestionnaireResponse | null,
): Map<string, TherapyMaterialAnswer> => {
    const answers = new Map<string, TherapyMaterialAnswer>();
    visitResponseItems(questionnaireResponse?.item, answers);
    return answers;
};

export const getInteractiveQuestionnaireItems = (questionnaire: Questionnaire): QuestionnaireItem[] =>
    questionnaire.item?.filter((item, index) => index > 0 && item.type !== 'display') ?? [];

export const findTherapyMaterialAnswer = ({
    answers,
    preferredLinkId,
    linkIdSuffixes = [],
    questionText,
}: {
    answers: Map<string, TherapyMaterialAnswer>;
    preferredLinkId?: string;
    linkIdSuffixes?: string[];
    questionText?: string;
}): TherapyMaterialAnswer | undefined => {
    if (preferredLinkId && answers.has(preferredLinkId)) {
        return answers.get(preferredLinkId);
    }

    const normalizedSuffixes = linkIdSuffixes.map((suffix) => suffix.toLowerCase());
    const normalizedQuestionText = questionText?.trim().toLowerCase();

    return Array.from(answers.values()).find((answer) => {
        const normalizedLinkId = answer.linkId.toLowerCase();
        if (normalizedSuffixes.some((suffix) => normalizedLinkId.endsWith(suffix))) {
            return true;
        }

        return Boolean(normalizedQuestionText && answer.text?.trim().toLowerCase() === normalizedQuestionText);
    });
};

export const getAnswerValues = (answer: QuestionnaireResponseItemAnswer): Array<string | number | boolean> => {
    if (answer.valueString !== undefined) return [answer.valueString];
    if (answer.valueInteger !== undefined) return [answer.valueInteger];
    if (answer.valueDecimal !== undefined) return [answer.valueDecimal];
    if (answer.valueBoolean !== undefined) return [answer.valueBoolean];
    if (answer.valueDate !== undefined) return [answer.valueDate];
    if (answer.valueDateTime !== undefined) return [answer.valueDateTime];
    if (answer.valueTime !== undefined) return [answer.valueTime];
    if (answer.valueUri !== undefined) return [answer.valueUri];
    if (answer.valueCoding) return [answer.valueCoding.display || answer.valueCoding.code || ''];
    if (answer.valueReference) return [answer.valueReference.display || answer.valueReference.reference || ''];
    if (answer.valueQuantity) {
        return [
            `${answer.valueQuantity.value ?? ''} ${
                answer.valueQuantity.unit || answer.valueQuantity.code || ''
            }`.trim(),
        ];
    }
    if (answer.valueAttachment) return [answer.valueAttachment.title || answer.valueAttachment.url || ''];
    return [];
};

export type AnswerLabels = {
    yes: string;
    no: string;
};

export const getAnswerText = (answer: TherapyMaterialAnswer | undefined, labels: AnswerLabels): string => {
    const values =
        answer?.item.answer?.flatMap((entry) =>
            getAnswerValues(entry).map((value) =>
                typeof value === 'boolean' ? (value ? labels.yes : labels.no) : String(value),
            ),
        ) ?? [];

    return values.filter(Boolean).join(', ');
};

export type ResolvedTherapyMaterialBlock = {
    item: TherapyMaterialContentItem;
    blockIndex: number;
    // One answer per input of the block: a textfield has one, an imageform one per row and
    // thoughtclouds one per prompt. Empty strings are inputs the patient left empty.
    answers: string[];
};

// Pairs every content block of the chosen version with the patient's answers. Both the preview
// and the PDF export render these blocks, so they always show the same answers.
export const resolveTherapyMaterialBlocks = ({
    document,
    audience,
    questionnaire,
    questionnaireResponse,
    labels,
}: {
    document: TherapyMaterialDocument;
    audience: TherapyMaterialAudience;
    questionnaire: Questionnaire;
    questionnaireResponse?: QuestionnaireResponse | null;
    labels: AnswerLabels;
}): ResolvedTherapyMaterialBlock[] => {
    const content = document.content[audience] ?? [];
    const answers = getTherapyMaterialAnswers(questionnaireResponse);
    const interactiveItems = getInteractiveQuestionnaireItems(questionnaire);
    let textFieldIndex = 0;

    return content.map((item, blockIndex) => {
        switch (item.type) {
            case 'textfield': {
                const fieldIndex = textFieldIndex;
                textFieldIndex += 1;
                const questionnaireItem = interactiveItems[fieldIndex];
                const answer = findTherapyMaterialAnswer({
                    answers,
                    preferredLinkId: questionnaireItem?.linkId,
                    linkIdSuffixes: [`-tf-${fieldIndex}`, `-textfield-${fieldIndex}`],
                    questionText: questionnaireItem?.text || item.text,
                });
                return { item, blockIndex, answers: [getAnswerText(answer, labels)] };
            }
            case 'imageform':
                return {
                    item,
                    blockIndex,
                    answers: Array.from({ length: item.rows ?? 5 }).map((_, rowIndex) =>
                        getAnswerText(
                            findTherapyMaterialAnswer({
                                answers,
                                linkIdSuffixes: [`-imageform-${blockIndex}-${rowIndex}`],
                            }),
                            labels,
                        ),
                    ),
                };
            case 'thoughtclouds':
                return {
                    item,
                    blockIndex,
                    answers: (item.prompts ?? []).map((prompt, promptIndex) =>
                        getAnswerText(
                            findTherapyMaterialAnswer({
                                answers,
                                linkIdSuffixes: [`-thoughtcloud-${blockIndex}-${promptIndex}`],
                                questionText: prompt,
                            }),
                            labels,
                        ),
                    ),
                };
            default:
                return { item, blockIndex, answers: [] };
        }
    });
};
